/* ===== Required CSS ===== */
import './styles/index.css';
import '@cyntler/react-doc-viewer/dist/index.css';
import 'react-data-grid/lib/styles.css';

/* Patch ResizeObserver to prevent "loop completed" errors triggered by
   react-data-grid when many rows are added at once (e.g. paste from Excel). */
( () => {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    constructor( callback: ResizeObserverCallback ) {
      super( ( entries, observer ) => {
        requestAnimationFrame( () => { callback( entries, observer ); } );
      } );
    }
  };
} )();

/* ===== Required Modules ===== */
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';

/* ===== Required Others ===== */
import { App } from './App';
import { i18n, persistor, store, mqttService, configService } from '@umanforge/common';
import { ToastProvider } from '@common_provider/toast.provider';
import { Loading } from '@common_components/loading.component';
import { PersistGate } from 'redux-persist/integration/react';

/*
 * Composant racine pour gérer l'initialisation et le chargement
 */
const Root = () => {

  const [ isReady, setIsReady ] = useState<boolean>( false );
  const [ isPageLoaded, setIsPageLoaded ] = useState<boolean>( false );

  useEffect( () => {
    const onInitialized = () => { setIsReady( true ); };
    if ( i18n.isInitialized ) {
      setIsReady( true );
    } else {
      i18n.on( 'initialized', onInitialized );
    }

    configService.getMQTTBrokerUrl().then( url => {
      mqttService.connect( url );
    } );

    return () => {
      i18n.off( 'initialized', onInitialized );
    };
  }, [] );

  useEffect( () => {
    if ( document.readyState === 'complete' ) {
      setIsPageLoaded( true );
    }
  }, [ document.readyState ] );

  if ( !isReady || !isPageLoaded ) {
    return (
      <div className='flex w-screen h-screen items-center justify-center'>
        <Loading size={ 200 } />
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <App />
      <ToastProvider />
    </I18nextProvider>
  );
};

// Création du root element pour le rendu de l'application
const root = ReactDOM.createRoot( document.getElementById( 'root' ) as HTMLElement );

root.render(
  <Provider store={ store }>
    <PersistGate loading={ <Loading /> } persistor={ persistor }>
      <Root />
    </PersistGate>
  </Provider>
);